/**
 * VE-016: tolerate common Zoom display-name formatting differences
 * (reordering, case/spacing/punctuation, appended suffixes) without
 * doing any fuzzy/partial matching — see story_VE_016 AC-4 for the
 * deliberate boundary.
 */

// Bracketed/parenthesised suffixes Zoom or participants append: pronouns,
// company name, role tags — e.g. "Alex Rivera (they/them)", "Alex Rivera [Acme]".
const BRACKETED_SUFFIX_RE = /\s*[([][^)\]]*[)\]]\s*/g;

// " - Company" / " – Role" style trailing suffix. Requires spaces around the
// dash so real hyphenated names ("Mary-Jane") are left untouched.
const DASH_SUFFIX_RE = /\s+[-–—]\s+.*$/;

// Possessive device suffixes Zoom clients commonly add: "'s iPhone", "'s iPad".
// Requires a space after the apostrophe-s so names like "O'Brien" are untouched.
const POSSESSIVE_SUFFIX_RE = /'s\s+\S+$/i;

// Anything that isn't a letter, digit, or whitespace, once suffixes are gone.
const PUNCTUATION_RE = /[^\p{L}\p{N}\s]/gu;

// Device names Zoom fills in when someone joins from a phone or tablet. The
// possessive form ("Khusi's iPhone") is already handled by POSSESSIVE_SUFFIX_RE,
// but the bare form ("khusi iphone") leaves a token that blocks every name tier.
const DEVICE_TOKENS = new Set([
  "iphone",
  "ipad",
  "ipod",
  "android",
  "phone",
  "mobile",
  "laptop",
  "macbook",
  "galaxy",
  "pixel",
  "tablet",
  "desktop",
  "pc",
]);

function stripKnownSuffixes(raw: string): string {
  return raw.replace(BRACKETED_SUFFIX_RE, " ").replace(DASH_SUFFIX_RE, "").replace(POSSESSIVE_SUFFIX_RE, "");
}

/**
 * Reduces a display name to a canonical, order-independent key: suffixes
 * stripped, punctuation removed, case/spacing ignored, tokens sorted.
 * "Rivera, Alex" and "Alex Rivera" and "  alex   RIVERA " all normalize
 * to the same key; "Sam" and "Samuel" do not (that's fuzzy matching,
 * out of scope — see VE-016 AC-4).
 */
export function normalizeName(raw: string | null | undefined): string {
  return nameTokens(raw).sort().join(" ");
}

/** The same cleanup as normalizeName, but returning the tokens themselves. */
export function nameTokens(raw: string | null | undefined): string[] {
  if (!raw) return [];
  const withoutSuffixes = stripKnownSuffixes(raw);
  const withoutPunctuation = withoutSuffixes.replace(PUNCTUATION_RE, " ");
  const tokens = withoutPunctuation.toLowerCase().split(/\s+/).filter(Boolean);
  // Only drop device tokens when something is left — someone whose whole display
  // name is "iPhone" still needs a key, however unhelpful.
  const withoutDevices = tokens.filter((token) => !DEVICE_TOKENS.has(token));
  return withoutDevices.length > 0 ? withoutDevices : tokens;
}

/**
 * Tokens meaningful enough to justify a partial match. Single characters
 * (initials, stray letters left by punctuation) carry too little signal —
 * matching on "a" alone would pair half the meeting.
 */
function significantTokens(raw: string | null | undefined): string[] {
  return nameTokens(raw).filter((token) => token.length > 1);
}

/**
 * VE-015 partial-name tier: true when one name's tokens are wholly contained in
 * the other's — "Rivera" vs "Alex Rivera", "Alex" vs "Alex Rivera". Whole
 * tokens only, so "Sam" never matches "Samuel"; that remains out of scope.
 * Callers must still enforce that only one live participant matches before
 * placing anyone (see findRawLiveCandidate).
 */
export function isNameSubset(a: string | null | undefined, b: string | null | undefined): boolean {
  const aTokens = significantTokens(a);
  const bTokens = significantTokens(b);
  if (aTokens.length === 0 || bTokens.length === 0) return false;
  const [shorter, longer] = aTokens.length <= bTokens.length ? [aTokens, bTokens] : [bTokens, aTokens];
  const longerSet = new Set(longer);
  return shorter.every((token) => longerSet.has(token));
}

// A Zoom name may compress a full name ("Khushi Yadav" -> "khushiy"), so compare
// compact forms rather than whole tokens.
//
// Four characters minimum, and no limit on how much longer the other form runs:
// a shared four-character stem is treated as the same person. That is a
// deliberate loosening — it also means "mohan" matches "mohandas". When both are
// in the meeting the caller sees two candidates and hands the choice to the
// operator (VE-015 AC-7); when only one is present the match is silent, which is
// the accepted cost of catching clipped and abbreviated Zoom names.
const MIN_PREFIX_LEN = 4;

/**
 * Every compact spelling of a name worth comparing.
 *
 * For "Khushi Yadav": each token ("khushi", "yadav"), the tokens run together
 * ("khushiyadav"), the same in sorted order so a reordered display name still
 * lines up ("khushiyadav" either way), first name plus trailing initials
 * ("khushiy"), and leading initials plus last name ("kyadav").
 */
function compactForms(raw: string | null | undefined): string[] {
  const tokens = significantTokens(raw);
  if (tokens.length === 0) return [];
  const forms = new Set<string>(tokens);
  forms.add(tokens.join(""));
  if (tokens.length > 1) {
    forms.add([...tokens].sort().join(""));
    forms.add(tokens[0] + tokens.slice(1).map((token) => token[0]).join(""));
    forms.add(tokens.slice(0, -1).map((token) => token[0]).join("") + tokens[tokens.length - 1]);
  }
  return Array.from(forms);
}

function isPrefixPair(a: string, b: string): boolean {
  const [shorter, longer] = a.length <= b.length ? [a, b] : [b, a];
  if (shorter.length < MIN_PREFIX_LEN) return false;
  return longer.startsWith(shorter);
}

/**
 * True when one name is a compact spelling of the other: "Khushi" vs "khushiy",
 * "Khushi Yadav" vs "khushiy". Strictly weaker than isNameSubset, so callers
 * must run it after that tier and must still refuse to place anyone when more
 * than one live participant matches (VE-015 AC-7).
 */
export function isNamePrefixMatch(a: string | null | undefined, b: string | null | undefined): boolean {
  const aForms = compactForms(a);
  const bForms = compactForms(b);
  if (aForms.length === 0 || bForms.length === 0) return false;
  return aForms.some((aForm) => bForms.some((bForm) => isPrefixPair(aForm, bForm)));
}
